import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Authenticator, useAuthenticator, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './AmplifyAuthWrapper.css';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { AppIconButton } from '../design-system';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useApp } from '../../context/AppContext';
import { sessionService } from '../../services/SessionService';
import { authService } from '../../services/AuthService';
import { appNavigate } from '../../utils/appNavigate';
import { parseTenantSlugFromPath } from '@cnapse/shared/utils/app-url.js';
import { createAmplifyTheme } from '../../theme/amplifyTheme';
import { readStoredThemeMode } from '../../theme/themeStorage';
import BrandLogo from '../design-system/BrandLogo';

function tenantHomePath(): string {
  const slug = parseTenantSlugFromPath(window.location.pathname);
  return slug ? `/${slug}/` : '/';
}

/**
 * Form field customization
 */
const formFields = {
  signIn: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email',
      isRequired: true,
    },
    password: {
      label: 'Password',
      placeholder: 'Enter your password',
      isRequired: true,
    },
  },
  signUp: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email',
      order: 1,
      isRequired: true,
    },
    password: {
      label: 'Password',
      placeholder: 'Enter your password (min 8 characters)',
      order: 2,
      isRequired: true,
    },
    confirm_password: {
      label: 'Confirm Password',
      placeholder: 'Confirm your password',
      order: 3,
      isRequired: true,
    },
    given_name: {
      label: 'First Name',
      placeholder: 'Enter your first name',
      order: 4,
      isRequired: true,
    },
    family_name: {
      label: 'Last Name',
      placeholder: 'Enter your last name',
      order: 5,
      isRequired: true,
    },
  },
  forceNewPassword: {
    password: {
      label: 'New Password',
      placeholder: 'Enter your new password',
      isRequired: true,
    },
  },
  resetPassword: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email',
      isRequired: true,
    },
  },
  confirmResetPassword: {
    confirmation_code: {
      label: 'Verification Code',
      placeholder: 'Enter the code from your email',
      isRequired: true,
    },
    password: {
      label: 'New Password',
      placeholder: 'Enter your new password',
      isRequired: true,
    },
    confirm_password: {
      label: 'Confirm Password',
      placeholder: 'Confirm your new password',
      isRequired: true,
    },
  },
};

/**
 * Component to handle user synchronization with backend database
 */
const UserSyncHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const { actions } = useApp();
  const syncAttemptedRef = React.useRef(false);

  useEffect(() => {
    // Prevent multiple sync attempts
    if (syncAttemptedRef.current) {
      return;
    }

    // Skip sync if user is already authenticated in localStorage
    // This prevents unnecessary sync on page reload
    const savedUser = localStorage.getItem('wafer_inventory_user');
    const accessToken = localStorage.getItem('wafer_access_token');
    
    if (savedUser && accessToken) {
      const pending = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
      if (pending) {
        sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
        appNavigate(pending);
        return;
      }
      if (window.location.pathname === '/' || window.location.pathname === '/login') {
        appNavigate(tenantHomePath());
      }
      return;
    }

    const syncUserWithBackend = async () => {
      try {
        if (user) {
          // Mark sync as attempted
          syncAttemptedRef.current = true;
          // Get Cognito access token
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();

          if (!accessToken) {
            throw new Error('No access token available');
          }

          // Store tokens in SessionService for subsequent API calls
          // Cognito tokens typically expire after 1 hour (3600 seconds)
          sessionService.saveTokensToStorage({
            accessToken,
            refreshToken: '', // Cognito handles refresh via Amplify SDK
            expiresIn: 3600, // 1 hour
            refreshExpiresIn: 2592000, // 30 days
          });

          console.log('Tokens stored in SessionService and localStorage');

          try {
            const data = await authService.syncCognitoUser({
              email: user.signInDetails?.loginId || user.username,
              cognitoSub: user.userId,
              accessToken,
            } as Parameters<typeof authService.syncCognitoUser>[0]);

            if (data.success && data.user) {
              const theme = readStoredThemeMode();
              actions.login({
                id: data.user.id,
                name: data.user.email.split('@')[0],
                email: data.user.email,
                role: data.user.role,
                firstName: data.user.firstName,
                lastName: data.user.lastName,
                phone: data.user.phone ?? undefined,
                avatar: data.user.avatarUrl,
                permissions: ['read', 'write', 'scan'],
                preferences: {
                  theme,
                  language: 'en',
                  notifications: true,
                  autoRefresh: true,
                },
              });

              const pending = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
              if (pending) {
                sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
                appNavigate(pending);
              } else {
                appNavigate(tenantHomePath());
              }
            }
          } catch (syncError) {
            const httpStatus = (syncError as { httpStatus?: number }).httpStatus;
            if (httpStatus === 404) {
              console.error('User not found in database');
              await signOut();
              localStorage.clear();
              sessionService.clearTokensFromStorage();
              alert('Your account is not registered in the system. Please contact your administrator.');
              navigate('/login', { replace: true });
              return;
            }

            if (
              syncError instanceof Error &&
              syncError.message.includes('Request timeout')
            ) {
              console.warn(
                'Backend sync timed out after 15s (non-critical). Cognito login succeeded.',
              );
            } else {
              console.warn('Backend sync failed (non-critical):', syncError);
            }
            appNavigate(tenantHomePath());
          }
        }
      } catch (error) {
        console.error('Failed to authenticate:', error);
        // Sign out on error
        await signOut();
        navigate('/login', { replace: true });
      }
    };

    syncUserWithBackend();
  }, [user, navigate, actions]);

  return <>{children}</>;
};

/**
 * Custom header component with theme toggle
 */
const CustomHeader: React.FC<{ theme: 'light' | 'dark'; onToggleTheme: () => void }> = ({ theme, onToggleTheme }) => {
  const muiTheme = useTheme();

  const themeToggle = (
    <AppIconButton
      onClick={onToggleTheme}
      aria-label="toggle theme"
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 1300,
        color: theme === 'dark' ? muiTheme.palette.common.white : muiTheme.palette.primary.main,
        '&:hover': {
          backgroundColor:
            theme === 'dark'
              ? alpha(muiTheme.palette.common.white, 0.1)
              : alpha(muiTheme.palette.primary.main, 0.12),
        },
      }}
    >
      {theme === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
    </AppIconButton>
  );

  return (
    <>
      {createPortal(themeToggle, document.body)}
      <div className="auth-header">
        <div className="auth-header-content">
          <div className="auth-header-text auth-header-brand">
            <BrandLogo variant="full" height={82} sx={{ maxWidth: 360 }} />            
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Custom footer component
 */
const CustomFooter = () => (
  <div className="auth-footer">
    <p>© {new Date().getFullYear()} Cnapse Cleanroom Intelligence System. All rights reserved.</p>
  </div>
);

interface AmplifyAuthWrapperProps {
  children: React.ReactNode;
}

/**
 * Main authentication wrapper using AWS Amplify UI Components
 * 
 * Features:
 * - Sign in (Sign up disabled - only admins can create users)
 * - Email verification
 * - Password reset
 * - MFA support (when enabled in Cognito)
 * - NEW_PASSWORD_REQUIRED challenge handling
 * - Automatic token refresh
 * - WCAG 2.1 AA accessibility
 * - Internationalization ready
 * - Light/Dark theme toggle
 * 
 * @param {AmplifyAuthWrapperProps} props
 */
const POST_AUTH_REDIRECT_KEY = 'postAuthRedirect';

const E2E_MOCK_ACCESS_TOKEN =
  'eyJhbGciOiJub25lIiwidHlwIjoiVVCJ9.eyJleHAiOjQxMDI0NDQ4MDAsInN1YiI6ImUyZS10ZXN0LWFjY2Vzcy10b2tlbiJ9.e2e';

function shouldBypassAmplifyAuthenticator(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return localStorage.getItem('wafer_access_token') === E2E_MOCK_ACCESS_TOKEN;
}

const PendingLabelRedirectCapture: React.FC = () => {
  useEffect(() => {
    if (/\/l\/[^/]+/i.test(window.location.pathname)) {
      sessionStorage.setItem(
        POST_AUTH_REDIRECT_KEY,
        window.location.pathname + window.location.search,
      );
    }
  }, []);
  return null;
};

const AmplifyAuthWrapper: React.FC<AmplifyAuthWrapperProps> = ({ children }) => {
  const { state, actions } = useApp();
  const theme = state.theme;
  const [bypassAuth] = React.useState(shouldBypassAmplifyAuthenticator);

  const toggleTheme = () => {
    actions.setTheme(theme === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    if (bypassAuth) {
      return;
    }

    // Listen to authentication events
    const hubListenerCancelToken = Hub.listen('auth', (data) => {
      const { payload } = data;
      
      switch (payload.event) {
        case 'signedIn':
          console.log('User signed in successfully');
          break;
        case 'signedOut':
          console.log('User signed out');
          break;
        case 'tokenRefresh':
          console.log('Auth tokens refreshed');
          break;
        case 'tokenRefresh_failure':
          console.error('Token refresh failed:', payload.data);
          break;
        case 'signInWithRedirect':
          console.log('Social sign-in redirect');
          break;
        case 'signInWithRedirect_failure':
          console.error('Social sign-in failed:', payload.data);
          break;
        default:
          break;
      }
    });

    // Cleanup listener on unmount
    return () => {
      hubListenerCancelToken();
    };
  }, [bypassAuth]);

  const amplifyTheme = useMemo(() => createAmplifyTheme(theme), [theme]);

  if (bypassAuth) {
    return <>{children}</>;
  }

  return (
    <ThemeProvider theme={amplifyTheme}>
      <PendingLabelRedirectCapture />
      <Authenticator
        formFields={formFields}
        components={{
          Header: () => <CustomHeader theme={theme} onToggleTheme={toggleTheme} />,
          Footer: CustomFooter,
        }}
        hideSignUp={true}
        variation="default"
      >
        {() => (
          <UserSyncHandler>
            {children}
          </UserSyncHandler>
        )}
      </Authenticator>
    </ThemeProvider>
  );
};

export default AmplifyAuthWrapper;
